<template>
  <tr class="text-sm" :class="[isFocused ? bgColorBySelectedProductType : bgColorByProductType]">
    <td class="pl-4 pr-2 sm:pl-6 sm:pr-4 py-5">
      <ValidationProvider
        :rules="index < 0 ? '' : 'required'"
        name="quantity"
        slim
        :vid="`lines.${index}.quantity`"
      >
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormInput
              type="Number"
              v-model="internalOfferLine.quantity"
              min="1"
              @change="update"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
    </td>
    <td class="py-5 whitespace-nowrap">&times;</td>
    <td class="px-2 sm:px-4 py-5">
      <ValidationProvider
        :rules="index < 0 ? '' : 'required'"
        name="description"
        slim
        :vid="`lines.${index}.description`"
      >
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormInput v-model="internalOfferLine.description" @change="update" />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
    </td>
    <td class="px-2 sm:px-4 py-5">
      <ValidationProvider
        :rules="index < 0 ? '' : 'required'"
        name="price per unit"
        slim
        :vid="`lines.${index}.pricePerUnit`"
      >
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormInput
              type="Number"
              v-model="internalOfferLine.pricePerUnit"
              min="0"
              step="0.01"
              class="w-24"
              @change="update"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
    </td>
    <td class="px-2 sm:px-4 py-5 text-right">
      {{ netAmountForHumans }}
    </td>
    <td class="px-2 sm:px-4 py-5">
      <ValidationProvider
        :rules="index < 0 ? '' : 'required'"
        name="vat rate"
        slim
        :vid="`lines.${index}.vatRate`"
      >
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormInput
              type="Number"
              v-model="internalOfferLine.vatRate"
              min="0"
              max="1.00"
              step="0.01"
              class="w-20"
              @change="update"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
    </td>
    <td class="px-2 sm:px-4 py-5 text-right">
      {{ vatAmountForHumans }}
    </td>
    <td class="px-2 sm:px-4 py-5 text-right">
      {{ totalForHumans }}
    </td>
    <td class="pr-4 pl-2 sm:pr-6 sm:pl-4 py-5">
      <div class="flex space-x-2 justify-end">
        <button
          v-if="isCurrent"
          type="button"
          class="h-8 w-8 inline-flex items-center justify-center bg-white text-gray-800 focus:bg-gray-800 focus:text-white focus:outline-none hover:bg-gray-800 hover:text-white rounded-full"
          @click="$emit('zoom-out')"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" class="inline-flex w-4 h-4">
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M7.828 11H20v2H7.828l5.364 5.364-1.414
              1.414L4 12l7.778-7.778 1.414 1.414z"
            />
          </svg>
        </button>
        <button
          v-else-if="hasChildren"
          type="button"
          class="h-8 w-8 inline-flex items-center justify-center bg-white text-gray-800 focus:bg-gray-800 focus:text-white focus:outline-none hover:bg-gray-800 hover:text-white rounded-full"
          @click="$emit('zoom-in', offerLineId)"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" class="inline-flex w-4 h-4">
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M16.172 11l-5.364-5.364 1.414-1.414L20
              12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z"
            />
          </svg>
        </button>
        <TableLineMoreActions
          :offer-line="internalOfferLine"
          @remove="remove"
          @update-supplementary-description="setSupplementaryDescription"
        />
      </div>
    </td>
  </tr>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import Numbro from '@/utils/numbro';
import TableLineMoreActions from './TableLineMoreActions.vue';

const defaultData = {
  quantity: 1,
  description: '',
  pricePerUnit: 0,
  vatRate: 0.24,
};

export default {
  components: {
    TableLineMoreActions,
  },
  props: {
    index: {
      type: Number,
      default: -1,
    },
    offerLine: {
      type: Object,
      default: null,
    },
    hasChildren: Boolean,
    isCurrent: Boolean,
    isFocused: Boolean,
  },
  data() {
    return {
      internalOfferLine: {
        ...defaultData,
      },
    };
  },
  computed: {
    offerLineId() {
      return this.offerLine.uuid || this.offerLine._uuid;
    },
    product() {
      if (!this.offerLine.productId) {
        return null;
      }

      return this.$store.state.entities.products.byId[this.offerLine.productId] || null;
    },
    bgColorBySelectedProductType() {
      if (this.product?.type === 'service') {
        return [
          'even:bg-yellow-50',
          'odd:bg-yellow-100',
          'first:border-t-2',
          'last:border-b-2',
          'border-yellow-600',
        ];
      }

      if (this.product?.type === 'basic') {
        return [
          'even:bg-blue-50',
          'odd:bg-blue-100',
          'first:border-t-2',
          'last:border-b-2',
          'border-blue-600',
        ];
      }

      return [
        'even:bg-gray-50',
        'odd:bg-gray-100',
        'first:border-t-2',
        'last:border-b-2',
        'border-gray-600',
      ];
    },
    bgColorByProductType() {
      if (this.product?.type === 'service') {
        return ['even:bg-yellow-50', 'odd:bg-yellow-100'];
      }

      if (this.product?.type === 'basic') {
        return ['even:bg-blue-50', 'odd:bg-blue-100'];
      }

      return ['even:bg-gray-50', 'odd:bg-gray-100'];
    },
    netAmount() {
      const { quantity = 1, pricePerUnit = 0 } = this.internalOfferLine;
      return quantity * pricePerUnit;
    },
    netAmountForHumans() {
      return Numbro(this.netAmount).formatCurrency({ mantissa: 2 });
    },
    vatAmount() {
      const { vatRate = 0 } = this.internalOfferLine;
      return this.netAmount * vatRate;
    },
    vatAmountForHumans() {
      return Numbro(this.vatAmount).formatCurrency({ mantissa: 2 });
    },
    total() {
      const { netAmount = 0, vatAmount = 0 } = this;
      return netAmount + vatAmount;
    },
    totalForHumans() {
      return Numbro(this.total).formatCurrency({ mantissa: 2 });
    },
  },
  watch: {
    offerLine: 'setLine',
  },
  created() {
    if (typeof this.offerLine === 'object' && this.offerLine !== null) {
      this.setLine(this.offerLine);
    }
  },
  methods: {
    setLine(userData = {}) {
      this.internalOfferLine = {
        ...defaultData,
        ...userData,
      };
    },
    setSupplementaryDescription(content) {
      this.internalOfferLine.supplementaryDescription = content;
      this.update();
    },
    insert() {
      this.$emit('insert', { ...this.internalOfferLine });
      this.setLine();
    },
    update() {
      this.$emit('update', { ...this.internalOfferLine });
    },
    remove() {
      if (this.isCurrent) {
        this.$emit('zoom-out');
      }
      this.$emit('remove', this.internalOfferLine);
    },
  },
};
</script>
